import { Checkbox, Field, FormLabel, Label, Paragraph } from '@quickmd-llc/design-system'
import type { UseFormWatch, UseFormSetValue, UseFormTrigger, FieldErrors } from 'react-hook-form'
import type { MedicationFormValues } from '../../types'
import { useGetAdminsFulfillmentpartners } from '@/api/generated/fulfillment-partner/fulfillment-partner'
import { FormSectionCard } from './FormSectionCard'

interface FulfillmentPartnersSectionProps {
  watch: UseFormWatch<MedicationFormValues>
  setValue: UseFormSetValue<MedicationFormValues>
  trigger: UseFormTrigger<MedicationFormValues>
  errors: FieldErrors<MedicationFormValues>
  showIndicator: boolean
  isValid: boolean
  touchedFields: Partial<Record<keyof MedicationFormValues, unknown>>
  isSubmitted: boolean
}

export function FulfillmentPartnersSection({
  watch,
  setValue,
  trigger,
  errors,
  showIndicator,
  isValid,
  touchedFields,
  isSubmitted,
}: FulfillmentPartnersSectionProps) {
  const { data: partners, isLoading } = useGetAdminsFulfillmentpartners()
  const selectedIds = watch('fulfillmentPartnerIds')

  function handleToggle(partnerId: string) {
    const newIds =
      selectedIds.includes(partnerId) ? selectedIds.filter((id) => id !== partnerId) : [...selectedIds, partnerId]
    setValue('fulfillmentPartnerIds', newIds, { shouldTouch: true })
    trigger('fulfillmentPartnerIds')
  }

  const showError = (touchedFields.fulfillmentPartnerIds || isSubmitted) && errors.fulfillmentPartnerIds

  return (
    <FormSectionCard title='Fulfillment partners' showIndicator={showIndicator} isValid={isValid}>
      {isLoading ?
        <Paragraph size='md' className='text-text-light'>
          Loading partners...
        </Paragraph>
      : !partners || partners.length === 0 ?
        <Paragraph size='md' className='text-text-light'>
          No fulfillment partners configured. Add partners from the medications list page.
        </Paragraph>
      : <div className='flex flex-col gap-sp-2'>
          {partners.map((partner) => (
            <Field key={partner.NcpdpId} className='flex items-center gap-sp-1.5'>
              <Checkbox
                checked={selectedIds.includes(partner.NcpdpId ?? '')}
                onChange={() => handleToggle(partner.NcpdpId ?? '')}
              />
              <FormLabel size='md'>{partner.Name}</FormLabel>
            </Field>
          ))}
          {showError ?
            <Label size='sm' className='text-text-error'>
              {errors.fulfillmentPartnerIds?.message}
            </Label>
          : null}
        </div>
      }
    </FormSectionCard>
  )
}
